import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Alert, Form, Modal } from 'react-bootstrap';
import { cachedRequest, cacheWithTTL, getCachedData, useDebounce } from '../utils/apiClient';
import axios from 'axios';
import { useImagePerformance, useImagePreloader } from '../hooks/useImagePerformance';
import PerformanceIndicator from '../components/PerformanceIndicator';
import RestaurantHead from '../components/RestaurantHead';

// CSS стили для горизонтальной прокрутки блюд и анимации корзины
const menuScrollStyles = `
  .menu-scroll-container {
    scrollbar-width: thin;
    scrollbar-color: #6c757d #f8f9fa;
    cursor: grab;
    user-select: none;
  }
  
  .menu-scroll-container:active {
    cursor: grabbing;
  }
  
  .menu-scroll-container::-webkit-scrollbar {
    height: 6px;
  }
  
  .menu-scroll-container::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 3px;
  }
  
  .menu-scroll-container::-webkit-scrollbar-thumb {
    background: #6c757d;
    border-radius: 3px;
  }
  
  .menu-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #495057;
  }
  
  @keyframes cartBounce {
    0% { transform: scale(1); }
    25% { transform: scale(1.3); }
    50% { transform: scale(1.1); }
    75% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
  
  .cart-button:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 6px 16px rgba(0,0,0,0.4) !important;
  }
  
  .waiter-call-button:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 6px 16px rgba(0,0,0,0.4) !important;
  }
  
  .cart-button, .waiter-call-button {
    box-sizing: border-box !important;
    border: none !important;
    outline: none !important;
    text-decoration: none !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
  }
  
  .bottom-button:hover {
    transform: scale(1.05) !important;
    box-shadow: 0 6px 16px rgba(0,0,0,0.4) !important;
  }
  
  .bottom-button {
    box-sizing: border-box !important;
    border: none !important;
    outline: none !important;
    text-decoration: none !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
  }
  
  .promotion-banner {
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    border-radius: 12px;
    padding: 16px;
    margin: 8px 0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    border: 1px solid rgba(255,255,255,0.3);
  }
  
  .promotion-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
  }
  
  .promotion-icon {
    font-size: 1.2rem;
    margin-right: 8px;
    color: #ff9800;
  }
  
  .promotion-title {
    color: #1976d2;
    font-weight: 600;
    font-size: 1rem;
    margin: 0;
  }
  
  .promotion-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  
  .promotion-discount {
    display: flex;
    align-items: center;
    color: #1976d2;
  }
  
  .promotion-discount-icon {
    font-size: 0.9rem;
    margin-right: 6px;
    color: #ff9800;
  }
  
  .promotion-discount-text {
    font-weight: 600;
  }
  
  .promotion-description {
    text-align: center;
    color: #666;
    font-size: 0.9rem;
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .promotion-description-icon {
    font-size: 0.8rem;
    margin-right: 6px;
    color: #ffc107;
  }
`;

const CustomerMenu = ({ subdomain, restaurant, isSubdomain = false, initialOrderType = 'dine_in' }) => {
  const { restaurantId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const menuIdParam = searchParams.get('menuId');
  const [menu, setMenu] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [cart, setCart] = useState([]);
  const [tableNumber, setTableNumber] = useState('');
  const [tableInfo, setTableInfo] = useState(null);
  const [orderType, setOrderType] = useState(initialOrderType); // 'dine_in' | 'delivery'
  const [isRestaurantOpen, setIsRestaurantOpen] = useState(true);
  const [workingHoursMessage, setWorkingHoursMessage] = useState('');
  const [waiterCallSent, setWaiterCallSent] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });
  const [showCartModal, setShowCartModal] = useState(false);
  const [cartAnimation, setCartAnimation] = useState(false);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [appliedPromotions, setAppliedPromotions] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const categoryNavRef = useRef(null);

  // Обработчики для drag-to-scroll
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.pageX,
      scrollLeft: e.currentTarget.scrollLeft
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX;
    const walk = (x - dragStart.x) * 2; // Скорость прокрутки
    e.currentTarget.scrollLeft = dragStart.scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Функция для автоматической прокрутки навигации к активной категории
  const scrollCategoryNavToActive = (category) => {
    if (!categoryNavRef.current) return;
    
    const navContainer = categoryNavRef.current;
    const categoryToFind = category || 'all';
    const activeButton = navContainer.querySelector(`[data-category="${categoryToFind}"]`);
    
    if (activeButton) {
      const containerRect = navContainer.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      
      // Проверяем, видна ли кнопка
      const isVisible = buttonRect.left >= containerRect.left && buttonRect.right <= containerRect.right;
      
      if (!isVisible) {
        // Прокручиваем к активной кнопке
        const scrollLeft = activeButton.offsetLeft - navContainer.offsetLeft - (navContainer.offsetWidth / 2) + (activeButton.offsetWidth / 2);
        navContainer.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  // Предотвращаем клик по кнопкам во время перетаскивания
  const handleButtonClick = (e, action) => {
    if (isDragging) {
      e.preventDefault();
      return;
    }
    action();
  };

  // Дебаунсинг для поиска
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Хуки для производительности изображений
  const { isSlowConnection, prefersReducedMotion, getImageSettings } = useImagePerformance();
  const { preloadImages } = useImagePreloader();

  // Определить ID ресторана
  const getRestaurantId = () => {
    if (isSubdomain && restaurant) {
      return restaurant._id;
    }
    return restaurantId;
  };

  // Проверяем, готовы ли данные для загрузки
  const isReadyToLoad = () => {
    if (isSubdomain) {
      return restaurant !== null && restaurant !== undefined;
    }
    return restaurantId !== null && restaurantId !== undefined;
  };

  // Загрузка информации о ресторане (должна быть первой для субдоменов)
  useEffect(() => {
    const loadRestaurantInfo = async () => {
      try {
        let restaurantData;
        
        if (isSubdomain && restaurant) {
          restaurantData = restaurant;
          // Если ресторан загружен из App.js, но нет defaultMenuId, загружаем полную информацию
          if (!restaurantData.defaultMenuId && restaurantData._id) {
            try {
              const subdomain = window.location.hostname.split('.')[0];
              const response = await fetch(`/api/restaurant/subdomain/${subdomain}`);
              restaurantData = await response.json();
            } catch (err) {
              console.error('Error loading full restaurant info:', err);
            }
          }
          
          // Если есть настройки меню по умолчанию для субдомена, устанавливаем orderType
          // Но НЕ перетираем явно выбранный режим (например, маршрут /delivery).
          if (restaurantData.defaultMenuType && initialOrderType === 'dine_in') {
            setOrderType(restaurantData.defaultMenuType);
          }
        } else {
          const currentRestaurantId = getRestaurantId();
          if (!currentRestaurantId) {
            console.error('Restaurant ID not found');
            return;
          }
          const response = await fetch(`/api/restaurant/${currentRestaurantId}`);
          restaurantData = await response.json();
        }
        
        setRestaurantInfo(restaurantData);
      } catch (error) {
        console.error('Error loading restaurant info:', error);
      }
    };

    if (isReadyToLoad()) {
      loadRestaurantInfo();
    }
  }, [isSubdomain, restaurant, restaurantId]);


  // Phone/address are collected on Checkout; omit here to keep cart identical across modes
  
  // Addons modal state
  const [addonsModalItem, setAddonsModalItem] = useState(null);
  const [selectedAddonsMap, setSelectedAddonsMap] = useState({}); // itemId -> string[] (addons)
  const [selectedGroupMap, setSelectedGroupMap] = useState({}); // itemId -> { [groupName]: string[] }

  const openAddons = (item) => {
    setAddonsModalItem(item);
    // initialize if not exists
    const set = new Set(selectedAddonsMap[item._id] || []);
    setSelectedAddonsMap((prev) => ({ ...prev, [item._id]: Array.from(set) }));
    const groups = selectedGroupMap[item._id] || {};
    setSelectedGroupMap((prev) => ({ ...prev, [item._id]: { ...groups } }));
  };
  const closeAddons = () => {
    if (addonsModalItem) {
      // Reset selected addons and groups for this item
      setSelectedAddonsMap(prev => {
        const newMap = { ...prev };
        delete newMap[addonsModalItem._id];
        return newMap;
      });
      setSelectedGroupMap(prev => {
        const newMap = { ...prev };
        delete newMap[addonsModalItem._id];
        return newMap;
      });
    }
    setAddonsModalItem(null);
  };

  // Функция для проверки времени работы ресторана
  const checkWorkingHours = (workingHours) => {
    if (!workingHours || !workingHours.restrictOrdersOutsideHours) {
      setIsRestaurantOpen(true);
      setWorkingHoursMessage('');
      return;
    }

    const now = new Date();
    const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
    
    const todaySchedule = workingHours[currentDay];
    
    if (!todaySchedule || todaySchedule.closed) {
      setIsRestaurantOpen(false);
      setWorkingHoursMessage('Ресторан закрыт сегодня');
      return;
    }

    const openTime = todaySchedule.open;
    const closeTime = todaySchedule.close;

    if (!openTime || !closeTime) {
      setIsRestaurantOpen(true);
      setWorkingHoursMessage('');
      return;
    }

    // Проверяем, находимся ли мы в рабочем времени
    const isOpen = currentTime >= openTime && currentTime <= closeTime;
    
    if (isOpen) {
      setIsRestaurantOpen(true);
      setWorkingHoursMessage('');
    } else {
      setIsRestaurantOpen(false);
      if (currentTime < openTime) {
        setWorkingHoursMessage(`Ресторан откроется в ${openTime}`);
      } else {
        setWorkingHoursMessage(`Ресторан закрыт до завтра (работает до ${closeTime})`);
      }
    }
  };
  const toggleAddon = (itemId, addonName) => {
    setSelectedAddonsMap((prev) => {
      const current = new Set(prev[itemId] || []);
      if (current.has(addonName)) current.delete(addonName); else current.add(addonName);
      return { ...prev, [itemId]: Array.from(current) };
    });
  };
  const toggleGroupOption = (itemId, group, optionName, type, maxSelect) => {
    setSelectedGroupMap((prev) => {
      const itemGroups = { ...(prev[itemId] || {}) };
      const current = new Set(itemGroups[group] || []);
      if (type === 'single') {
        // single selection
        itemGroups[group] = [optionName];
      } else {
        if (current.has(optionName)) current.delete(optionName); else {
          if (maxSelect && current.size >= maxSelect) {
            // do nothing if reached limit
            return prev;
          }
          current.add(optionName);
        }
        itemGroups[group] = Array.from(current);
      }
      return { ...prev, [itemId]: itemGroups };
    });
  };
  
  const fetchMenu = useCallback(async () => {
    try {
      const currentRestaurantId = getRestaurantId();
      if (!currentRestaurantId) {
        setError('ID ресторана не найден');
        setLoading(false);
        return;
      }

      // Проверяем кэш сначала
      const cacheKey = `menu_${currentRestaurantId}_${orderType}_${menuIdParam || 'default'}`;
      const cachedMenu = getCachedData(cacheKey);
      
      // Принудительно очищаем кэш для отладки или если есть параметр refresh
      const urlParams = new URLSearchParams(window.location.search);
      const forceRefresh = urlParams.get('refresh') === 'true';
      
      if (menuIdParam || forceRefresh) {
        localStorage.removeItem(cacheKey);
      }
      
      if (cachedMenu && !menuIdParam && !forceRefresh) {
        setMenu(cachedMenu);
        // Ensure UI mode matches cached menu mode
        if (cachedMenu?.mode && ['dine_in', 'delivery'].includes(cachedMenu.mode)) {
          setOrderType(cachedMenu.mode);
        }
        // Не перезаписываем restaurantInfo — favicon/branding берутся из loadRestaurantInfo()
        
        // Проверяем время работы ресторана
        if (cachedMenu.branding?.workingHours) {
          checkWorkingHours(cachedMenu.branding.workingHours);
        }
        
        // Загружаем группы опций из кэша
        const cachedGroups = getCachedData(`groupOptions_${currentRestaurantId}`);
        if (cachedGroups) {
          setGroupOptions(cachedGroups);
        }
        
        setLoading(false);
        return;
      }

        let res;
        // Приоритет 1: menuId из URL параметра
        if (menuIdParam) {
          res = await cachedRequest(`/api/menu/by-id/${menuIdParam}`);
          if (res.data?.mode) {
            setOrderType(res.data.mode);
          }
        } else {
          // Приоритет 2: defaultMenuId из настроек ресторана (для субдоменов)
          let restaurantData = restaurantInfo;
          if (isSubdomain && !restaurantData && restaurant) {
            restaurantData = restaurant;
          }
          
          // Если есть defaultMenuId, используем его напрямую
          if (isSubdomain && restaurantData?.defaultMenuId) {
            const defaultMenuId = restaurantData.defaultMenuId._id || restaurantData.defaultMenuId;
            res = await cachedRequest(`/api/menu/by-id/${defaultMenuId}`);
            if (res.data?.mode) {
              setOrderType(res.data.mode);
            } else if (restaurantData.defaultMenuType) {
              setOrderType(restaurantData.defaultMenuType);
            }
          } else {
            // Приоритет 3: Старая логика - загрузка по режиму
            res = await cachedRequest(`/api/menu/${currentRestaurantId}?mode=${orderType}`);
          }
        }
      
        setMenu(res.data);
      cacheWithTTL(cacheKey, res.data, 10 * 60 * 1000); // 10 минут кэш для меню
        // Не перезаписываем restaurantInfo — favicon и branding берутся из loadRestaurantInfo()
      
      // Проверяем время работы ресторана
      if (res.data.branding?.workingHours) {
        checkWorkingHours(res.data.branding.workingHours);
      }
      
      // Загрузить группы опций
      try {
        let groupRes;
        
        if (isSubdomain && restaurant) {
          // Для субдоменов используем специальный endpoint
          const subdomain = window.location.hostname.split('.')[0];
          groupRes = await cachedRequest(`/api/group-options/subdomain/${subdomain}`);
        } else {
          // Для обычных маршрутов используем restaurantId
          groupRes = await cachedRequest(`/api/group-options/${restaurantId}`);
        }
        setGroupOptions(groupRes.data);
        const cacheKey = isSubdomain ? `groupOptions_subdomain_${restaurant._id}` : `groupOptions_${restaurantId}`;
        cacheWithTTL(cacheKey, groupRes.data, 30 * 60 * 1000); // 30 минут кэш для групп
      } catch (err) {
        console.error('Error fetching group options:', err);
      }
      
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Ошибка загрузки меню');
        setLoading(false);
      }
  }, [getRestaurantId, orderType, menuIdParam, isSubdomain, restaurant, restaurantInfo]);
    
  // Загрузка активных акций
  const fetchPromotions = useCallback(async () => {
    if (!menu?._id) return;

    try {
      const response = await axios.get(`/api/promotions/menu/${menu._id}/active`);
      setPromotions(response.data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  }, [menu?._id]);

  // Загружаем меню при монтировании компонента
  // Для обычных маршрутов (не субдоменов)
  useEffect(() => {
    if (!isSubdomain && isReadyToLoad()) {
      fetchMenu();
    }
  }, [fetchMenu, isReadyToLoad, isSubdomain]);

  // Для субдоменов загружаем меню после загрузки restaurantInfo
  useEffect(() => {
    if (isSubdomain && restaurantInfo) {
      fetchMenu();
    }
  }, [fetchMenu, isSubdomain, restaurantInfo]);

  // Загружаем акции после загрузки меню
  useEffect(() => {
    if (menu?._id) {
      fetchPromotions();
    }
  }, [fetchPromotions]);

  // Держим акции свежими (удаление/редактирование должно сразу отражаться в баннерах)
  useEffect(() => {
    const onFocus = () => fetchPromotions();
    window.addEventListener('focus', onFocus);
    const id = setInterval(() => fetchPromotions(), 60 * 1000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(id);
    };
  }, [fetchPromotions]);

  // Keep orderType aligned with the actual loaded menu (covers cache and async races)
  useEffect(() => {
    const m = menu?.mode;
    if (m && ['dine_in', 'delivery'].includes(m) && m !== orderType) {
      setOrderType(m);
    }
  }, [menu?.mode]);

  const freeDeliveryPromotion = useMemo(
    () => promotions.find((p) => p?.type === 'free_delivery') || null,
    [promotions]
  );

  // Функция для расчета скидок
  const calculatePromotions = useCallback(() => {
    
    if (!promotions.length || !cart.length) {
      setAppliedPromotions([]);
      return;
    }

    const applicablePromotions = [];
    const cartTotal = cart.reduce((sum, item) => {
      const source = (menu.items || []).find(it => it._id === item._id);
      const addonsTotal = (item.addons || []).reduce((s, name) => {
        const found = source?.addons?.find(a => a.name === name);
        return s + (found ? Number(found.price) : 0);
      }, 0);
      const groupTotal = Object.entries(item.addonGroups || {}).reduce((s, [groupName, groupOptions]) => {
        if (Array.isArray(groupOptions)) {
          return s + groupOptions.reduce((gs, option) => gs + Number(option.price || 0), 0);
        }
        return s;
      }, 0);
      return sum + (Number(item.price) + addonsTotal + groupTotal) * item.quantity;
    }, 0);

    promotions.forEach(promotion => {
      
      const minAmount = promotion.type === 'free_delivery'
        ? Number(promotion.minDeliveryAmount || 0)
        : Number(promotion.minOrderAmount || 0);
      if (minAmount > 0 && cartTotal < minAmount) return;

      let discount = 0;
      let message = '';

      switch (promotion.type) {
        case 'percentage':
          // Фильтруем товары по категориям для расчета скидки
          const percentageApplicableItems = cart.filter(item => 
            promotion.applicableCategories.length === 0 || 
            promotion.applicableCategories.includes(item.category)
          );
          
          const percentageCartTotal = percentageApplicableItems.reduce((sum, item) => {
            const itemPrice = item.price;
            const addonPrice = Object.entries(item.addonGroups || {}).reduce((addonSum, [groupName, addons]) => {
              return addonSum + (addons || []).reduce((addonGroupSum, addon) => addonGroupSum + (addon.price || 0), 0);
            }, 0);
            return sum + (itemPrice + addonPrice) * item.quantity;
          }, 0);
          
          discount = (percentageCartTotal * promotion.discountValue) / 100;
          if (promotion.maxDiscountAmount && discount > promotion.maxDiscountAmount) {
            discount = promotion.maxDiscountAmount;
          }
          message = `Скидка ${promotion.discountValue}%`;
          break;

        case 'fixed_amount':
          // Фильтруем товары по категориям для расчета скидки
          const fixedApplicableItems = cart.filter(item => 
            promotion.applicableCategories.length === 0 || 
            promotion.applicableCategories.includes(item.category)
          );
          
          const fixedCartTotal = fixedApplicableItems.reduce((sum, item) => {
            const itemPrice = item.price;
            const addonPrice = Object.entries(item.addonGroups || {}).reduce((addonSum, [groupName, addons]) => {
              return addonSum + (addons || []).reduce((addonGroupSum, addon) => addonGroupSum + (addon.price || 0), 0);
            }, 0);
            return sum + (itemPrice + addonPrice) * item.quantity;
          }, 0);
          
          discount = promotion.discountValue;
          if (discount > fixedCartTotal) {
            discount = fixedCartTotal;
          }
          message = `Скидка ${promotion.discountValue} BYN`;
          break;

        case 'buy_x_get_y':
          const applicableItems = cart.filter(item => {
            // Проверяем привязку к конкретным товарам
            const isItemApplicable = promotion.applicableItems.length === 0 || 
              promotion.applicableItems.includes(item._id);
            
            // Проверяем привязку к категориям
            const isCategoryApplicable = promotion.applicableCategories.length === 0 || 
              promotion.applicableCategories.includes(item.category);
            
            return isItemApplicable && isCategoryApplicable;
          });
          
          
          // Подсчитываем общее количество товаров (с учетом количества)
          const totalItemQuantity = applicableItems.reduce((sum, item) => sum + item.quantity, 0);
          
          if (totalItemQuantity >= promotion.buyQuantity) {
            // Логика: купи X товаров, получи Y товаров по специальной цене
            const sortedItems = applicableItems.sort((a, b) => a.price - b.price);
            discount = 0;
            
            // Создаем массив всех товаров с учетом количества
            const allItems = [];
            sortedItems.forEach(item => {
              for (let i = 0; i < item.quantity; i++) {
                allItems.push({ ...item, quantity: 1 });
              }
            });
            
            // Применяем акцию: каждые buyQuantity товаров, getQuantity товаров по специальной цене
            const specialPrice = promotion.specialPrice && promotion.specialPrice > 0 ? promotion.specialPrice : 0;
            
            // Сортируем товары по цене (от дешевых к дорогим) для правильного применения скидки
            const sortedAllItems = [...allItems].sort((a, b) => a.price - b.price);
            
            // Вычисляем количество полных циклов акции
            const totalGroups = Math.floor(sortedAllItems.length / promotion.buyQuantity);
            
            // Применяем скидку к getQuantity самым дешевым товарам за каждый полный цикл
            // Например: 6 товаров, buyQuantity=2, getQuantity=1 → 3 цикла × 1 скидка = 3 скидки
            const itemsToDiscount = Math.min(totalGroups * promotion.getQuantity, sortedAllItems.length);
            for (let j = 0; j < itemsToDiscount; j++) {
              const cheapestItem = sortedAllItems[j];
              if (cheapestItem.price > specialPrice) {
                discount += cheapestItem.price - specialPrice;
              }
            }
            
            
            if (promotion.specialPrice && promotion.specialPrice > 0) {
              message = `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity} по ${promotion.specialPrice} BYN`;
            } else {
              message = `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity} бесплатно`;
            }
          } else {
          }
          break;

        case 'free_delivery':
          discount = 0;
          message = 'Бесплатная доставка';
          break;
      }


      if (discount > 0 || promotion.type === 'free_delivery') {
        applicablePromotions.push({
          ...promotion,
          discount,
          message
        });
      } else {
      }
    });

    setAppliedPromotions(applicablePromotions);
  }, [promotions, cart, menu]);

  // Пересчитываем акции при изменении корзины или акций
  useEffect(() => {
    calculatePromotions();
  }, [calculatePromotions]);

  // Автопрокрутка слайдшоу
  useEffect(() => {
    const slideshow = menu?.branding?.slideshow;
    if (slideshow?.enabled && slideshow?.autoplay && slideshow?.images?.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlideIndex((prevIndex) => 
          (prevIndex + 1) % slideshow.images.length
        );
      }, slideshow.interval || 3000);
      
      return () => clearInterval(interval);
    }
  }, [menu?.branding?.slideshow]);

  // Предзагрузка изображений для первых элементов меню
  useEffect(() => {
    if (menu?.items && !isSlowConnection) {
      const firstItems = menu.items.slice(0, 6);
      const imageUrls = firstItems
        .filter(item => item.image)
        .map(item => item.image);
      
      if (imageUrls.length > 0) {
        preloadImages(imageUrls);
      }
    }
  }, [menu, isSlowConnection, preloadImages]);
  
  // Prefill table number from ?table= query if present
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tn = params.get('table');
      if (tn) {
        setTableNumber(tn);
      }
    } catch (e) {
      console.error('Error parsing URL params:', e);
    }
  }, [restaurantId]);

  // Получение информации о столе
  const fetchTableInfo = async (tableNum) => {
    if (!tableNum || !restaurantId) return;
    
    try {
      const response = await axios.get(`/api/tables/public/${restaurantId}/${tableNum}`);
      setTableInfo(response.data);
    } catch (error) {
      console.error('Error fetching table info:', error);
      setTableInfo(null);
    }
  };

  // Вызываем fetchTableInfo при изменении tableNumber
  useEffect(() => {
    if (tableNumber) {
      fetchTableInfo(tableNumber);
    }
  }, [tableNumber, restaurantId]);
  
  // Мемоизированная фильтрация элементов меню
  const filteredItems = useMemo(() => {
    if (!menu?.items) return [];
    
    let items = menu.items;
    
    // Фильтрация по поиску
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term)
      );
    }
    
    return items;
  }, [menu?.items, debouncedSearchTerm]);

  // Мемоизированное группирование по категориям
  const itemsByCategory = useMemo(() => {
    const grouped = {};
    filteredItems.forEach(item => {
      if (item.status === 'hidden') return;
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    return grouped;
  }, [filteredItems]);

  // Мемоизированные отсортированные категории
  const sortedCategories = useMemo(() => {
    const categories = Object.keys(itemsByCategory || {});
    return categories.sort((a, b) => {
      const categoryA = menu?.categories?.find(cat => cat.name === a);
      const categoryB = menu?.categories?.find(cat => cat.name === b);
      const orderA = Number(categoryA?.order) ?? 999;
      const orderB = Number(categoryB?.order) ?? 999;
      
      return orderA - orderB;
    });
  }, [itemsByCategory, menu?.categories]);

  // Отслеживание прокрутки для обновления активной категории
  useEffect(() => {
    const handleScroll = () => {
      if (debouncedSearchTerm.trim()) return; // Не отслеживаем во время поиска
      
      const categories = sortedCategories;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      let currentCategory = '';
      
      // Находим текущую категорию на основе позиции прокрутки
      categories.forEach(category => {
        const element = document.getElementById(`category-${category}`);
        if (element) {
          const elementTop = element.offsetTop - 170;
          if (scrollTop >= elementTop) {
            currentCategory = category;
          }
        }
      });
      
      if (currentCategory !== activeCategory) {
        setActiveCategory(currentCategory);
        // Автоматически прокручиваем навигацию к активной категории
        setTimeout(() => scrollCategoryNavToActive(currentCategory), 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sortedCategories, activeCategory, debouncedSearchTerm]);

  // Сброс активной категории при поиске
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      setActiveCategory('');
    }
  }, [debouncedSearchTerm]);

  const addToCart = useCallback((item, addons, addonGroups) => {
    // Проверяем, открыт ли ресторан
    if (!isRestaurantOpen) {
      alert(`Нельзя добавить товар в корзину: ${workingHoursMessage}`);
      return;
    }

    // Анимация корзины
    setCartAnimation(true);
    setTimeout(() => setCartAnimation(false), 600);

    // Check if item is already in cart
    const addonsKey = JSON.stringify((addons || []).slice().sort((a,b)=>a.localeCompare(b)));
    const groupsKey = JSON.stringify(addonGroups || {});
    const existingItem = cart.find(cartItem => cartItem._id === item._id && JSON.stringify((cartItem.addons||[]).slice().sort((a,b)=>a.localeCompare(b))) === addonsKey && JSON.stringify(cartItem.addonGroups || {}) === groupsKey);
    
    if (existingItem) {
      // Increase quantity
      setCart(cart.map(cartItem => 
        cartItem._id === item._id && JSON.stringify((cartItem.addons||[]).slice().sort((a,b)=>a.localeCompare(b))) === addonsKey && JSON.stringify(cartItem.addonGroups || {}) === groupsKey
          ? { ...cartItem, quantity: cartItem.quantity + 1 } 
          : cartItem
      ));
    } else {
      // Add new item to cart
      const cartItem = { ...item, quantity: 1, addons: addons || [], addonGroups: addonGroups || {} };
      setCart([...cart, cartItem]);
    }
  }, [cart, isRestaurantOpen, workingHoursMessage]);
  
  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item._id !== itemId));
  };
  
  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }
    
    setCart(cart.map(item => {
      if (item._id === itemId) {
        const updatedItem = { ...item, quantity };
        return updatedItem;
      }
      return item;
    }));
  };

  // Функция для редактирования товара в корзине
  const editCartItem = (cartItem) => {
    setEditingCartItem(cartItem);
    // Инициализируем состояние дополнений для редактирования
    const set = new Set(cartItem.addons || []);
    setSelectedAddonsMap((prev) => ({ ...prev, [cartItem._id]: Array.from(set) }));
    const groups = cartItem.addonGroups || {};
    setSelectedGroupMap((prev) => ({ ...prev, [cartItem._id]: { ...groups } }));
  };

  // Функция для сохранения изменений товара в корзине
  const saveCartItemChanges = () => {
    if (!editingCartItem) return;

    const updatedAddons = selectedAddonsMap[editingCartItem._id] || [];
    const updatedGroups = selectedGroupMap[editingCartItem._id] || {};

    setCart(cart.map(item => {
      if (item._id === editingCartItem._id) {
        return {
          ...item,
          addons: updatedAddons,
          addonGroups: updatedGroups
        };
      }
      return item;
    }));

    setEditingCartItem(null);
  };

  // Функция для отмены редактирования
  const cancelCartItemEdit = () => {
    setEditingCartItem(null);
    // Очищаем временные состояния
    setSelectedAddonsMap(prev => {
      const newMap = { ...prev };
      delete newMap[editingCartItem?._id];
      return newMap;
    });
    setSelectedGroupMap(prev => {
      const newMap = { ...prev };
      delete newMap[editingCartItem?._id];
      return newMap;
    });
  };
  
  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const source = (menu.items || []).find(it => it._id === item._id);
      
      
      // Simple addons total
      const addonsTotal = (item.addons || []).reduce((s, name) => {
        const found = source?.addons?.find(a => a.name === name);
        const price = found ? Number(found.price) : 0;
        return s + price;
      }, 0);
      
      // Addon groups total (from item's addonGroups)
      const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
        const group = source?.addonGroups?.find(g => g.name === gname);
        if (!group) return acc;
        const optsByName = new Map(group.options.map(o => [o.name, o]));
        const add = (list || []).reduce((s, nm) => {
          const price = optsByName.get(nm)?.price || 0;
          return s + price;
        }, 0);
        return acc + add;
      }, 0);
      
      // Group options total (from GroupOption model) - only if not already counted in groupsTotal
      const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
        // Skip if this group is already counted in groupsTotal
        const group = source?.addonGroups?.find(g => g.name === gname);
        if (group) {
          return acc; // Already counted in groupsTotal
        }
        
        if (!source?.groupOptionIds) {
          return acc;
        }
        
        // Find group option by name
        const groupOption = groupOptions.find(g => g.name === gname);
        if (!groupOption) {
          return acc;
        }
        
        const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
        const add = (list || []).reduce((s, nm) => {
          const price = optsByName.get(nm)?.price || 0;
          return s + price;
        }, 0);
        return acc + add;
      }, 0);
      
      const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
      const itemTotal = unit * item.quantity;
      
      
      return total + itemTotal;
    }, 0);
  };

  // Функция для расчета итоговой суммы с учетом скидок
  const getCartTotalWithDiscounts = () => {
    const subtotal = getCartTotal();
    const totalDiscount = appliedPromotions.reduce((sum, promotion) => sum + promotion.discount, 0);
    const finalTotal = Math.max(0, subtotal - totalDiscount);
    
    
    return finalTotal;
  };
  
  const handlePlaceOrder = async () => {
    
    if (!cart.length) {
      alert('Корзина пуста');
      return;
    }

    // Validation for address/phone/table will be performed on Checkout step

    // Persist checkout data and navigate to checkout page for unified flow
    try {
      
      localStorage.setItem('cart', JSON.stringify(cart));
      localStorage.setItem('restaurant', JSON.stringify(restaurant || { _id: restaurantId }));
      localStorage.setItem('menu', JSON.stringify(menu)); // Сохраняем меню с брендингом
      localStorage.setItem('groupOptionsSnapshot', JSON.stringify(groupOptions)); // Сохраняем GroupOptions
      localStorage.setItem('orderType', orderType);
      localStorage.setItem('tableNumber', String(tableNumber || ''));
      // Delivery contact details will be entered on Checkout
      localStorage.setItem('menuId', menuIdParam || '');
      
      window.location.href = '/checkout';
    } catch (e) {
      console.error('Ошибка подготовки заказа', e);
      alert('Не удалось подготовить заказ. Попробуйте снова.');
    }
  };

  // Функция вызова официанта
  // Функция для прокрутки к категории
  const scrollToCategory = (category) => {
    const element = document.getElementById(`category-${category}`);
    if (element) {
      // Добавляем небольшой отступ для sticky меню
      const offsetTop = element.offsetTop - 140;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setActiveCategory(category);
    }
  };

  const callWaiter = async () => {
    try {
      const currentTableNumber = tableNumber || 'Не указан';
      const helpMessage = 'Требуется помощь';
      
      const payload = {
        restaurantId: restaurantId,
        tableNumber: currentTableNumber,
        message: helpMessage,
        timestamp: new Date().toISOString(),
        orderType: orderType,
        cartItems: cart.length,
        totalAmount: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)
      };


      // Отправляем уведомление на мобильное приложение
      await axios.post('/api/waiter/call', payload);

      setWaiterCallSent(true);
      alert(`Официант вызван к столу ${currentTableNumber}! Ожидайте, пожалуйста.`);
      
      // Сброс состояния через 30 секунд
      setTimeout(() => {
        setWaiterCallSent(false);
      }, 30000);
      
    } catch (error) {
      console.error('Ошибка вызова официанта:', error);
      alert('Не удалось вызвать официанта. Попробуйте снова.');
    }
  };
  
  if (loading) {
    return <div className="mt-5 text-center">Загрузка меню...</div>;
  }
  
  if (error) {
    return <div className="mt-5 text-center text-danger">{error}</div>;
  }
  
  if (!menu) {
    return <div className="mt-5 text-center">Меню не найдено</div>;
  }

  // Проверка статуса стола
  if (tableNumber && tableInfo && !tableInfo.isActive) {
    return (
      <div className="mt-5 text-center">
        <div className="alert alert-warning">
          <h4>Стол временно недоступен</h4>
          <p>Стол № {tableNumber} в данный момент неактивен.</p>
          <p>Обратитесь к персоналу ресторана для получения помощи.</p>
        </div>
      </div>
    );
  }

  // Branding data (logo, footer, ads, contacts)
  const branding = menu.branding || {};
  
  // Theme data
  const theme = branding.theme || {};
  const backgroundImage = branding.backgroundImage || {};
  
  // Единообразные обработчики цветов
  const colors = {
    primary: theme.primary || '#007bff',
    secondary: theme.secondary || '#6c757d',
    background: theme.background || '#ffffff',
    surface: theme.surface || '#ffffff',
    text: theme.text || '#212529',
    border: theme.border || '#dee2e6',
    success: theme.success || '#28a745',
    error: theme.error || '#dc3545',
    opacity: theme.opacity || 0.9
  };
  
  
  // Apply custom styles
  const customStyles = {
    backgroundColor: colors.background,
    color: colors.text,
    minHeight: '100vh',
    position: 'relative',
    paddingTop: '60px' // Добавляем margin-top с учетом фонового изображения
  };
  
  // Background image styles
  const backgroundImageStyles = backgroundImage.url ? {
    backgroundImage: `url("${backgroundImage.url}")`,
    backgroundPosition: backgroundImage.position || 'center',
    backgroundSize: backgroundImage.size || 'cover',
    backgroundRepeat: backgroundImage.repeat || 'no-repeat',
    backgroundAttachment: 'scroll', // Changed from 'fixed' to 'scroll' for better mobile support
    backgroundOrigin: 'border-box'
  } : {};
  
  // Container overlay styles
  const containerOverlayStyles = backgroundImage.url ? {
    backgroundColor: `rgba(255, 255, 255, ${1 - (backgroundImage.opacity || 0.1)})`,
    borderRadius: '10px',
    padding: '20px',
   // margin: '20px 0',
    position: 'relative',
    zIndex: 1
  } : {};
  
  return (
    <div style={{ 
      ...customStyles, 
      ...backgroundImageStyles
    }}>
      <style>{menuScrollStyles}</style>
      <RestaurantHead 
        restaurantBranding={restaurantInfo?.branding} 
        restaurantName={restaurantInfo?.restaurantName}
        restaurantId={restaurantInfo?._id}
      />
      <Container className="main-content" style={containerOverlayStyles}>
        {/* Компактная кнопка корзины */}
        <div
          className="bottom-button"
          onClick={() => isRestaurantOpen ? setShowCartModal(true) : alert(`Нельзя открыть корзину: ${workingHoursMessage}`)}
          style={{
            position: 'fixed',
            bottom: 'var(--pwa-bar-height, 0px)',
            marginBottom: '30px!important',
            right: '20px',
            zIndex: 1000,
            borderRadius: '10px',
            width: '120px',
            height: '45px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            border: 'none',
            fontSize: '1rem',
            fontWeight: 'bold',
            transition: 'all 0.3s ease',
            backgroundColor: isRestaurantOpen ? '#007bff' : '#6c757d',
            color: '#ffffff',
            transform: cartAnimation ? 'scale(1.2)' : 'scale(1)',
            animation: cartAnimation ? 'cartBounce 0.6s ease-in-out' : 'none',
            margin:'10px',
            padding: '8px 12px',
            lineHeight: '1',
            verticalAlign: 'baseline',
            boxSizing: 'border-box',
            cursor: isRestaurantOpen ? 'pointer' : 'not-allowed',
            userSelect: 'none',
            opacity: isRestaurantOpen ? 1 : 0.6
          }}
        >
          🛒 Корзина
          {cart.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                fontSize: '0.7rem',
                minWidth: '20px',
                height: '20px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                padding: '2px 4px',
                backgroundColor: '#dc3545',
                color: '#ffffff'
              }}
            >
              {(() => {
                const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
                return totalItems > 99 ? '99+' : totalItems;
              })()}
            </div>
          )}
        </div>
      <Row className="mb-3 text-center">
        <Col>
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt="Логотип"
              style={{ maxHeight: '80px', objectFit: 'contain' }}
              className="mb-2"
            />
          )}
          <h1 className="mt-2" style={{ color: colors.primary }}>
            {restaurantInfo?.restaurantName || 'Меню ресторана'}
          </h1>
          {restaurantInfo?.restaurantDescription && (
            <p className="mb-0" style={{ color: colors.secondary }}>
              {restaurantInfo?.restaurantDescription}
            </p>
          )}
        </Col>
      </Row>

      {/* Slideshow */}
      {branding?.slideshow?.enabled && branding?.slideshow?.images?.length > 0 && (
        <Row className="mb-4">
          <Col>
            <div 
              className="position-relative rounded overflow-hidden"
              style={{ 
                height: '300px',
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              {/* Current slide */}
              <img
                src={branding.slideshow.images[currentSlideIndex]?.url}
                alt={branding.slideshow.images[currentSlideIndex]?.alt || `Слайд ${currentSlideIndex + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'opacity 0.5s ease-in-out'
                }}
              />
              
              {/* Navigation arrows */}
              {branding.slideshow.showArrows && branding.slideshow.images.length > 1 && (
                <>
                  <button
                    className="position-absolute top-50 start-0 translate-middle-y btn btn-light rounded-circle border-0 shadow"
                    style={{ 
                      width: '40px', 
                      height: '40px',
                      zIndex: 10,
                      marginLeft: '10px'
                    }}
                    onClick={() => setCurrentSlideIndex(
                      currentSlideIndex === 0 
                        ? branding.slideshow.images.length - 1 
                        : currentSlideIndex - 1
                    )}
                  >
                    ‹
                  </button>
                  <button
                    className="position-absolute top-50 end-0 translate-middle-y btn btn-light rounded-circle border-0 shadow"
                    style={{ 
                      width: '40px', 
                      height: '40px',
                      zIndex: 10,
                      marginRight: '10px'
                    }}
                    onClick={() => setCurrentSlideIndex(
                      (currentSlideIndex + 1) % branding.slideshow.images.length
                    )}
                  >
                    ›
                  </button>
                </>
              )}
              
              {/* Indicators */}
              {branding.slideshow.showIndicators && branding.slideshow.images.length > 1 && (
                <div 
                  className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-2 mb-3"
                  style={{ zIndex: 10 }}
                >
                  {branding.slideshow.images.map((_, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm rounded-circle border-0 ${
                        index === currentSlideIndex ? 'btn-primary' : 'btn-light'
                      }`}
                      style={{ width: '12px', height: '12px' }}
                      onClick={() => setCurrentSlideIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      {/* Поле поиска */}
      <Row className="mb-3">
        <Col>
          <Form.Control
            type="text"
            placeholder="Поиск по меню..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              color: colors.text
            }}
          />
        </Col>
      </Row>

      {/* Навигация по категориям */}
      {!debouncedSearchTerm.trim() && sortedCategories.length > 1 && (
        <div 
          className="mb-3"
          style={{
            position: 'sticky',
            top: '10px',
            zIndex: 100,
            backgroundColor: `${colors.surface}95`, // 95% прозрачности цвета поверхности
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '8px',
            marginBottom: '20px',
            boxShadow: `0 2px 8px ${colors.text}20`, // Тень в цвете текста с прозрачностью
            border: `1px solid ${colors.border}`,
            color: colors.text
          }}
        >
                <div 
                  ref={categoryNavRef}
                  className="d-flex overflow-auto pb-2 horizontal-scroll menu-scroll-container"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  <Button
                    data-category="all"
                    variant={!activeCategory ? "primary" : "outline-primary"}
                    size="sm"
                    className="me-2 flex-shrink-0"
                    onClick={(e) => handleButtonClick(e, () => {
                      window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                      });
                      setActiveCategory('');
                    })}
                    style={{
                      backgroundColor: !activeCategory ? colors.success : 'transparent',
                      borderColor: colors.success,
                      color: !activeCategory ? '#ffffff' : colors.success,
                      borderRadius: '20px',
                      padding: '6px 16px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                      boxShadow: !activeCategory ? `0 2px 4px ${colors.success}30` : 'none'
                    }}
                  >
                    Все 🍽️
                  </Button>
                  {sortedCategories.map((category, index) => (
                    <Button
                      key={category}
                      data-category={category}
                      variant={activeCategory === category ? "primary" : "outline-primary"}
                      size="sm"
                      className="me-2 flex-shrink-0"
                      onClick={(e) => handleButtonClick(e, () => scrollToCategory(category))}
                      style={{
                        backgroundColor: activeCategory === category ? colors.success : 'transparent',
                        borderColor: colors.success,
                        color: activeCategory === category ? '#ffffff' : colors.success,
                        borderRadius: '20px',
                        padding: '6px 16px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        boxShadow: activeCategory === category ? `0 2px 4px ${colors.success}30` : 'none'
                      }}
                    >
                      {category}
                    </Button>
                  ))}
                </div>
        </div>
      )}

      {branding.adText && (
        <Alert variant="info" className="text-center">{branding.adText}</Alert>
      )}

      {/* Сообщение о времени работы */}
      {!isRestaurantOpen && workingHoursMessage && (
        <Alert variant="warning" className="text-center">
          <strong>⚠️ {workingHoursMessage}</strong>
        </Alert>
      )}

      {/* Информация о доставке */}
      {menu?.mode === 'delivery' && freeDeliveryPromotion && (
        <div className="mb-4">
          <div 
            className="border rounded p-3 shadow-sm"
            style={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
              border: '1px solid #bbdefb'
            }}
          >
            <div className="text-center mb-2">
              <span className="fs-4">🚚</span>
              <h6 className="d-inline-block ms-2 mb-0 fw-bold" style={{ color: '#1976d2' }}>
                Информация о доставке
              </h6>
            </div>
            
            <div className="row g-2">
              {Number(freeDeliveryPromotion.minDeliveryAmount || 0) > 0 && (
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="me-2">💰</span>
                    <span className="fw-semibold" style={{ color: '#424242' }}>
                      Мин. заказ: 
                    </span>
                    <span className="ms-1 fw-bold" style={{ color: '#1976d2' }}>
                      {Number(freeDeliveryPromotion.minDeliveryAmount || 0)} BYN
                    </span>
                  </div>
                </div>
              )}
              
              {Number(freeDeliveryPromotion.deliveryFee || 0) > 0 && (
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="me-2">📦</span>
                    <span className="fw-semibold" style={{ color: '#424242' }}>
                      Стоимость: 
                    </span>
                    <span className="ms-1 fw-bold" style={{ color: '#1976d2' }}>
                      {Number(freeDeliveryPromotion.deliveryFee || 0)} BYN
                    </span>
                  </div>
                </div>
              )}
              
              {Number(freeDeliveryPromotion.minDeliveryAmount || 0) === 0 && Number(freeDeliveryPromotion.deliveryFee || 0) === 0 && (
                <div className="col-12">
                  <div className="text-center">
                    <span className="me-2">🎉</span>
                    <span className="fw-bold fs-5" style={{ color: '#4caf50' }}>
                      Бесплатная доставка
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {Number(freeDeliveryPromotion.minDeliveryAmount || 0) > 0 && (
              <div className="text-center mt-2">
                <small className="text-muted">
                  💡 При заказе от {Number(freeDeliveryPromotion.minDeliveryAmount || 0)} BYN доставка бесплатна
                </small>
              </div>
            )}
          </div>
        </div>
      )}
       
      <Row>
        <Col md={12}>
          
          {/* Categories Content */}
          {sortedCategories.map(category => (
            <div key={category} id={`category-${category}`} className="mb-5">
              <h6 
                className="mb-3 slide-up" 
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border,
                  color: colors.primary,
                  opacity: colors.opacity,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: `1px solid ${colors.border}`,
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  animationDelay: `${sortedCategories.indexOf(category) * 0.1}s`
                }}
              >
                {category}
              </h6>
              {/* Временно отключена виртуализация из-за ошибок в react-window */}
              <div 
                className="d-flex overflow-auto pb-2 horizontal-scroll menu-scroll-container"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              >
                {(itemsByCategory[category] || []).map((item, index) => (
                  <div 
                    key={item._id} 
                    className="me-3 flex-shrink-0"
                    style={{ minWidth: '280px', maxWidth: '320px' }}
                  >
                      <Card 
                      className="h-100 slide-up" 
                      style={{ 
                        backgroundColor: colors.surface,
                        borderColor: colors.border,
                        color: colors.text,
                        opacity: colors.opacity,
                        minHeight: '420px',
                        display: 'flex',
                        flexDirection: 'column',
                        animationDelay: `${index * 0.1}s`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: '12px',
                        border: 'none'
                      }}
                    >
                      {item.image && (
                        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
                          <Card.Img 
                            variant="top" 
                            src={item.image} 
                            alt={item.name} 
                            style={{ 
                              height: '180px', 
                              objectFit: 'cover',
                              flexShrink: 0,
                              transition: 'transform 0.3s ease',
                              userSelect: 'none',
                              pointerEvents: 'none',
                              WebkitUserDrag: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              msUserSelect: 'none'
                            }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                            className=""
                          />
                          {/* Image overlay for better text readability */}
                          <div 
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 50%)',
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      )}
                      <Card.Body 
                        className="d-flex flex-column"
                        style={{ flex: 1 }}
                      >
                        {/* Header Section - Fixed Height */}
                        <div style={{ minHeight: '50px', marginBottom: '8px' }}>
                          <Card.Title 
                            className="mb-1" 
                            style={{ 
                              color: colors.primary,
                              fontSize: '1.1rem',
                              fontWeight: '600',
                              lineHeight: '1.3'
                            }}
                          >
                            {item.name}
                          </Card.Title>
                        </div>

                        {/* Description Section - Scrollable with increased height */}
                        <div 
                          style={{ 
                            minHeight: '100px', 
                            maxHeight: '120px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            marginBottom: '12px',
                            paddingRight: '4px'
                          }}
                        >
                          {item.description && item.description.trim() && (
                            <Card.Text 
                              style={{ 
                                fontSize: '0.9rem',
                                lineHeight: '1.5',
                                color: colors.text + ' !important',
                                margin: 0,
                                wordWrap: 'break-word'
                              }}
                            >
                              {item.description}
                            </Card.Text>
                          )}
                          {(() => {
                            const weight = item.weight;
                            return weight && String(weight).trim() ? (
                              <div 
                                className="text-muted mt-2" 
                                style={{ fontSize: '0.85rem' }}
                              >
                                Вес: {weight} {item.weightUnit || 'г'}
                              </div>
                            ) : null;
                          })()}
                        </div>

                        {/* Spacer to push buttons to bottom */}
                        <div style={{ flex: 1 }}></div>

                        {/* Price and Status Section - Fixed Height */}
                        <div 
                          className="d-flex justify-content-between align-items-center mb-3"
                          style={{ minHeight: '40px' }}
                        >
                          <h5 
                            className="mb-0" 
                            style={{ 
                              color: colors.success,
                              fontSize: '1.2rem',
                              fontWeight: '700'
                            }}
                          >
                            {item.price.toFixed(2)} BYN
                          </h5>
                          {item.status === 'coming_soon' ? (
                            <Badge bg="warning" text="dark">Скоро</Badge>
                          ) : !item.isAvailable ? (
                            <Badge bg="secondary">Недоступно</Badge>
                          ) : null}
                        </div>

                        {/* Buttons Section - Fixed Height */}
                        <div style={{ minHeight: '40px' }}>
                          {item.status === 'coming_soon' || !item.isAvailable ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="w-100"
                              disabled
                              style={{
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                opacity: 0.6
                              }}
                            >
                              {item.status === 'coming_soon' ? 'Скоро в продаже' : 'Недоступно'}
                            </Button>
                          ) : (
                            <div className="d-flex gap-2">
                              {(Array.isArray(item.addons) && item.addons.length > 0) || 
                               (Array.isArray(item.addonGroups) && item.addonGroups.length > 0) ||
                               (Array.isArray(item.groupOptionIds) && item.groupOptionIds.length > 0) ? (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className={`${(
                                    (Array.isArray(item.addons) && item.addons.length > 0) || 
                                    (Array.isArray(item.addonGroups) && item.addonGroups.length > 0) ||
                                    (Array.isArray(item.groupOptionIds) && item.groupOptionIds.length > 0)
                                  ) ? "flex-fill" : "w-100"}`}
                                  onClick={(e) => handleButtonClick(e, () => openAddons(item))}
                                  disabled={!isRestaurantOpen}
                                  style={{ 
                                    backgroundColor: isRestaurantOpen ? colors.success : colors.secondary,
                                    borderColor: isRestaurantOpen ? colors.success : colors.secondary,
                                    color: '#ffffff',
                                    fontSize: '0.8rem',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isRestaurantOpen ? '0 2px 4px rgba(0,123,255,0.3)' : 'none'
                                   }}
                                >
                                  Дополнения
                                </Button>
                              ) : null}
                            <Button
                              variant="primary"
                              size="sm"
                                className={`${(
                                  (Array.isArray(item.addons) && item.addons.length > 0) || 
                                  (Array.isArray(item.addonGroups) && item.addonGroups.length > 0) ||
                                  (Array.isArray(item.groupOptionIds) && item.groupOptionIds.length > 0)
                                ) ? "flex-fill" : "w-100"}`}
                                onClick={(e) => handleButtonClick(e, () => addToCart(item, (selectedAddonsMap[item._id] || []), (selectedGroupMap[item._id] || {})))}
                                disabled={!isRestaurantOpen}
                                style={{ 
                                  backgroundColor: isRestaurantOpen ? colors.success : colors.secondary,
                                  borderColor: isRestaurantOpen ? colors.success : colors.secondary,
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  transition: 'all 0.2s ease',
                                  boxShadow: isRestaurantOpen ? '0 2px 4px rgba(0,123,255,0.3)' : 'none'
                                }}
                              >
                                В корзину
                            </Button>
                            </div>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Col>
      </Row>

      {(branding.footerText || (branding.contact && (branding.contact.phone || branding.contact.email || branding.contact.address || branding.contact.website)) || (branding.social && (branding.social.instagram || branding.social.facebook || branding.social.telegram || branding.social.vk || branding.social.whatsapp)) || (branding.workingHours && Object.values(branding.workingHours || {}).some(day => day && !day.closed))) && (
        <div className="mt-4 pt-3 border-top text-center" style={{ color: colors.text }}>
          {branding.footerText && <div className="mb-2" style={{ color: colors.text }}>{branding.footerText}</div>}
          
          {/* Contact Information */}
          {(branding.contact && (branding.contact.phone || branding.contact.email || branding.contact.address || branding.contact.website)) && (
            <div className="mb-3">
              <h6 className="mb-2" style={{ color: colors.text }}>📞 Контакты</h6>
          <div style={{ fontSize: 14, color: colors.text }}>
            {branding.contact?.phone && <div>Телефон: {branding.contact.phone}</div>}
            {branding.contact?.email && <div>Email: {branding.contact.email}</div>}
            {branding.contact?.address && <div>Адрес: {branding.contact.address}</div>}
            {branding.contact?.website && <div>УНП: {branding.contact.website}</div>}
          </div>
        </div>
      )}

          {/* Social Media */}
          {(branding.social && (branding.social.instagram || branding.social.facebook || branding.social.telegram || branding.social.vk || branding.social.whatsapp)) && (
            <div className="mb-3">
              <h6 className="mb-2" style={{ color: colors.text }}>Мы в социальных сетях</h6>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                {branding.social?.instagram && (
                  <a href={`https://instagram.com/${branding.social.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    <span className="badge bg-gradient" style={{ background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)' }}>
                      📷 Instagram
                    </span>
                  </a>
                )}
                {branding.social?.facebook && (
                  <a href={`https://facebook.com/${branding.social.facebook}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    <span className="badge bg-primary">📘 Facebook</span>
                  </a>
                )}
                {branding.social?.telegram && (
                  <a href={`https://t.me/${branding.social.telegram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    <span className="badge bg-info">Telegram</span>
                  </a>
                )}
                {branding.social?.vk && (
                  <a href={`https://vk.com/${branding.social.vk}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    <span className="badge bg-primary">🔵 VKontakte</span>
                  </a>
                )}
                {branding.social?.whatsapp && (
                  <a href={`https://wa.me/${branding.social.whatsapp.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    <span className="badge bg-success">💬 WhatsApp</span>
                  </a>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Cart Modal */}
      <Modal 
        show={showCartModal} 
        onHide={() => setShowCartModal(false)} 
        size="lg"
        centered
        style={{ zIndex: 1050 }}
      >
        <Modal.Header 
          closeButton 
          style={{ 
            backgroundColor: colors.surface,
            borderColor: colors.border,
            color: colors.text
          }}
        >
          <Modal.Title style={{ color: colors.primary }}>
            🛒 Корзина ({cart.reduce((sum, item) => sum + item.quantity, 0)} товаров)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body 
          style={{ 
            backgroundColor: colors.surface,
            color: colors.text,
            maxHeight: '60vh',
            overflowY: 'auto'
          }}
        >
          {cart.length === 0 ? (
            <div className="text-center py-4">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛒</div>
              <p style={{ color: colors.text, fontSize: '1.1rem' }}>Ваша корзина пуста</p>
              <p style={{ color: colors.secondary }}>Добавьте товары из меню</p>
            </div>
          ) : (
            <>
              {cart.map((item, index) => (
                <div key={`${item._id}-${index}`} className="border-bottom py-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div style={{ flex: 1 }}>
                      <h6 style={{ color: colors.text, fontWeight: '600', marginBottom: '0.5rem' }}>
                        {item.name}
                      </h6>
                      <div style={{ color: colors.success, fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {(() => {
                          const source = (menu.items || []).find(it => it._id === item._id);
                          
                          // Simple addons total
                          const addonsTotal = (item.addons || []).reduce((s, name) => {
                            const found = source?.addons?.find(a => a.name === name);
                            return s + (found ? Number(found.price) : 0);
                          }, 0);
                          
                          // Addon groups total (from item's addonGroups)
                          const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                            const group = source?.addonGroups?.find(g => g.name === gname);
                            if (!group) return acc;
                            const optsByName = new Map(group.options.map(o => [o.name, o]));
                            const add = (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                            return acc + add;
                          }, 0);
                          
                          // Group options total (from GroupOption model)
                          const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                            const group = source?.addonGroups?.find(g => g.name === gname);
                            if (group) return acc; // Already counted in groupsTotal
                            
                            if (!source?.groupOptionIds) return acc;
                            
                            const groupOption = groupOptions.find(g => g.name === gname);
                            if (!groupOption) return acc;
                            
                            const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
                            const add = (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                            return acc + add;
                          }, 0);
                          
                          const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
                          return (unit * item.quantity).toFixed(2) + ' BYN';
                        })()}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => updateQuantity(item._id, item.quantity - 1)}
                        style={{ borderRadius: '50%', width: '32px', height: '32px' }}
                      >
                        -
                      </Button>
                      <span style={{ minWidth: '30px', textAlign: 'center', fontWeight: 'bold' }}>
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => updateQuantity(item._id, item.quantity + 1)}
                        style={{ borderRadius: '50%', width: '32px', height: '32px' }}
                      >
                        +
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeFromCart(item._id)}
                        style={{ borderRadius: '8px' }}
                      >
                        🗑️
                      </Button>
                      {/* Кнопка редактирования дополнений */}
                      {((item.addons && item.addons.length > 0) || 
                        (item.addonGroups && Object.keys(item.addonGroups).length > 0)) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => editCartItem(item)}
                          disabled={!isRestaurantOpen}
                          style={{ borderRadius: '8px' }}
                          title="Редактировать дополнения"
                        >
                          ✏️
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Дополнения */}
                  {!!(item.addons && item.addons.length) && (
                    <div className="small mb-2" style={{ color: colors.secondary }}>
                      <strong>Дополнения:</strong> {item.addons.map(name => {
                        const source = (menu.items || []).find(it => it._id === item._id);
                        const found = source?.addons?.find(a => a.name === name);
                        const price = found ? Number(found.price) : 0;
                        return price > 0 ? `${name} (+${price.toFixed(2)} BYN)` : name;
                      }).join(', ')}
                    </div>
                  )}
                  
                  {/* Группы опций */}
                  {item.addonGroups && Object.keys(item.addonGroups || {}).length > 0 && (
                    <div className="small mb-2" style={{ color: colors.secondary }}>
                      {Object.entries(item.addonGroups || {}).map(([g, list]) => (
                        <div key={g}>
                          <strong>{g}:</strong> {(list || []).map(optName => {
                            const source = (menu.items || []).find(it => it._id === item._id);
                            let price = 0;
                            
                            // Check in item's addonGroups first
                            const group = source?.addonGroups?.find(gr => gr.name === g);
                            if (group) {
                              const option = group.options.find(o => o.name === optName);
                              price = option ? Number(option.price) : 0;
                            } else {
                              // Check in GroupOption model
                              const groupOption = groupOptions.find(gr => gr.name === g);
                              if (groupOption) {
                                const option = groupOption.options.find(o => o.name === optName);
                                price = option ? Number(option.price) : 0;
                              }
                            }
                            
                            return price > 0 ? `${optName} (+${price.toFixed(2)} BYN)` : optName;
                          }).join(', ')}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Акции и скидки */}
              {appliedPromotions.filter(p => p.type !== 'free_delivery').length > 0 && (
                <div className="mt-4 pt-3 border-top">
                  <h6 style={{ color: colors.text, marginBottom: '1rem' }}>
                    <i className="fas fa-percent me-2" style={{ color: colors.success }}></i>
                    Применённые акции:
                  </h6>
                  {appliedPromotions.filter(p => p.type !== 'free_delivery').map((promotion, index) => (
                    <div key={index} className="promotion-banner">
                      <div className="promotion-header">
                        <i className={`fas ${promotion.type === 'free_delivery' ? 'fa-truck' : 'fa-percent'} promotion-icon`}></i>
                        <h6 className="promotion-title">{promotion.title}</h6>
                      </div>
                      
                      <div className="promotion-details">
                        <div className="promotion-discount">
                          <i className={`fas ${promotion.type === 'free_delivery' ? 'fa-gift' : 'fa-tag'} promotion-discount-icon`}></i>
                          <span className="promotion-discount-text">
                            {promotion.type === 'free_delivery' ? 'Бесплатная доставка' : `Скидка: -${promotion.discount.toFixed(2)} BYN`}
                          </span>
                        </div>
                      </div>
                      
                      {promotion.description && (
                        <div className="promotion-description">
                          <i className="fas fa-lightbulb promotion-description-icon"></i>
                          <span>{promotion.description}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}


                {/* Итого */}
              <div className="mt-4 pt-3 border-top">
                {appliedPromotions.filter(p => p.type !== 'free_delivery').length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ color: colors.text }}>Сумма без скидок:</span>
                    <span style={{ color: colors.text, textDecoration: 'line-through' }}>
                      {getCartTotal().toFixed(2)} BYN
                    </span>
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center">
                  <h5 style={{ color: colors.text, margin: 0 }}>Итого:</h5>
                  <h4 style={{ color: colors.success, margin: 0, fontWeight: 'bold' }}>
                    {getCartTotalWithDiscounts().toFixed(2)} BYN
                  </h4>
                </div>
                {appliedPromotions.filter(p => p.type !== 'free_delivery').length > 0 && (
                  <div className="text-center mt-2">
                    <small style={{ color: colors.success, fontWeight: '600' }}>
                      Вы экономите: {appliedPromotions.filter(p => p.type !== 'free_delivery').reduce((sum, p) => sum + p.discount, 0).toFixed(2)} BYN
                    </small>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer 
          style={{ 
            backgroundColor: colors.surface,
            borderColor: colors.border,
            justifyContent: 'space-between'
          }}
        >
          <Button 
            variant="secondary" 
            onClick={() => setShowCartModal(false)}
            style={{ borderRadius: '8px' }}
          >
            Продолжить покупки
          </Button>
          {cart.length > 0 && (
            <Button
              variant="success"
              onClick={() => {
                setShowCartModal(false);
                handlePlaceOrder();
              }}
              disabled={!isRestaurantOpen}
              style={{ 
                backgroundColor: isRestaurantOpen ? colors.success : colors.secondary,
                borderColor: isRestaurantOpen ? colors.success : colors.secondary,
                borderRadius: '8px',
                fontWeight: '600',
                minWidth: '150px'
              }}
            >
              {orderType === 'delivery' ? 'Оформить доставку' : 'Оформить заказ'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal для редактирования дополнений товара в корзине */}
      <Modal 
        show={!!editingCartItem} 
        onHide={cancelCartItemEdit} 
        centered
        size="lg"
      >
        <Modal.Header 
          closeButton 
          style={{ 
            backgroundColor: colors.surface,
            borderColor: colors.border,
            color: colors.text
          }}
        >
          <Modal.Title style={{ color: colors.primary }}>
            ✏️ Редактировать: {editingCartItem?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body 
          style={{ 
            backgroundColor: colors.surface,
            color: colors.text
          }}
        >
          {editingCartItem && (
            <>
              {/* Simple addons */}
              {Array.isArray(editingCartItem.addons) && editingCartItem.addons.length > 0 && (
                <div className="mb-3">
                  <div className="fw-semibold mb-1">Дополнения</div>
                  {editingCartItem.addons.map((a) => (
                    <Form.Check
                      key={a.name}
                      type="checkbox"
                      className="mb-2"
                      id={`edit-addon-${editingCartItem._id}-${a.name}`}
                      label={`${a.name} (+${Number(a.price).toFixed(2)} BYN)`}
                      checked={new Set(selectedAddonsMap[editingCartItem._id] || []).has(a.name)}
                      onChange={() => toggleAddon(editingCartItem._id, a.name)}
                    />
                  ))}
                </div>
              )}

              {/* Groups */}
              {Array.isArray(editingCartItem.addonGroups) && editingCartItem.addonGroups.length > 0 && (
                <div className="mb-1">
                  <div className="fw-semibold mb-2">Группы опций</div>
                  {editingCartItem.addonGroups.map((g) => (
                    <div key={g.name} className="mb-3">
                      <div className="mb-1">
                        {g.name} {g.required ? <span className="text-danger">*</span> : null}
                        {g.type === 'multiple' && g.maxSelect ? (
                          <span className="text-muted small"> (макс. {g.maxSelect})</span>
                        ) : null}
                      </div>
                      {(g.options || []).map((opt) => (
                        <Form.Check
                          key={opt.name}
                          type={g.type === 'single' ? 'radio' : 'checkbox'}
                          name={`edit-g-${editingCartItem._id}-${g.name}`}
                          id={`edit-g-${editingCartItem._id}-${g.name}-${opt.name}`}
                          className="mb-1"
                          label={`${opt.name} (+${Number(opt.price).toFixed(2)} BYN)`}
                          checked={new Set((selectedGroupMap[editingCartItem._id]?.[g.name]) || []).has(opt.name)}
                          onChange={() => toggleGroupOption(editingCartItem._id, g.name, opt.name, g.type, g.maxSelect)}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Группы опций из GroupOption */}
              {Array.isArray(editingCartItem.groupOptionIds) && editingCartItem.groupOptionIds.length > 0 && (
                <div className="mb-1">
                  <div className="fw-semibold mb-2">Дополнения:</div>
                  {editingCartItem.groupOptionIds.map((groupId) => {
                    const group = groupOptions.find(g => g._id === groupId);
                    if (!group) return null;
                    
                    return (
                      <div key={group._id} className="mb-3">
                        <div className="mb-1">
                          {group.name}
                          {group.options.some(opt => opt.isRequired) && <span className="text-danger"> *</span>}
                        </div>
                        {group.options.map((opt) => (
                          <Form.Check
                            key={opt.name}
                            type={group.options.some(o => o.isMultiple) ? 'checkbox' : 'radio'}
                            name={`edit-group-${editingCartItem._id}-${group._id}`}
                            id={`edit-group-${editingCartItem._id}-${group._id}-${opt.name}`}
                            className="mb-1"
                            label={`${opt.name} ${opt.price > 0 ? `(+${Number(opt.price).toFixed(2)} BYN)` : ''}`}
                            checked={new Set((selectedGroupMap[editingCartItem._id]?.[group.name]) || []).has(opt.name)}
                            onChange={() => toggleGroupOption(editingCartItem._id, group.name, opt.name, opt.isMultiple ? 'multiple' : 'single', undefined)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer 
          style={{ 
            backgroundColor: colors.surface,
            borderColor: colors.border,
            justifyContent: 'space-between'
          }}
        >
          <Button 
            variant="secondary" 
            onClick={cancelCartItemEdit}
            style={{ borderRadius: '8px' }}
          >
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={saveCartItemChanges}
            style={{ 
              backgroundColor: colors.success,
              borderColor: colors.success,
              borderRadius: '8px',
              fontWeight: '600'
            }}
          >
            Сохранить изменения
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Addons modal */}
      <Modal show={!!addonsModalItem} onHide={closeAddons} centered>
        <Modal.Header closeButton>
          <Modal.Title>Дополнения: {addonsModalItem?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addonsModalItem && (
            <>
              {/* Simple addons */}
              {Array.isArray(addonsModalItem.addons) && addonsModalItem.addons.length > 0 && (
                <div className="mb-3">
                  <div className="fw-semibold mb-1">Дополнения</div>
                  {addonsModalItem.addons.map((a) => (
                    <Form.Check
                      key={a.name}
                      type="checkbox"
                      className="mb-2"
                      id={`addon-${addonsModalItem._id}-${a.name}`}
                      label={`${a.name} (+${Number(a.price).toFixed(2)} BYN)`}
                      checked={new Set(selectedAddonsMap[addonsModalItem._id] || []).has(a.name)}
                      onChange={() => toggleAddon(addonsModalItem._id, a.name)}
                    />
                  ))}
                </div>
              )}

              {/* Groups */}
              {Array.isArray(addonsModalItem.addonGroups) && addonsModalItem.addonGroups.length > 0 && (
                <div className="mb-1">
                  <div className="fw-semibold mb-2">Группы опций</div>
                  {addonsModalItem.addonGroups.map((g) => (
                    <div key={g.name} className="mb-3">
                      <div className="mb-1">
                        {g.name} {g.required ? <span className="text-danger">*</span> : null}
                        {g.type === 'multiple' && g.maxSelect ? (
                          <span className="text-muted small"> (макс. {g.maxSelect})</span>
                        ) : null}
                      </div>
                      {(g.options || []).map((opt) => (
                        <Form.Check
                          key={opt.name}
                          type={g.type === 'single' ? 'radio' : 'checkbox'}
                          name={`g-${addonsModalItem._id}-${g.name}`}
                          id={`g-${addonsModalItem._id}-${g.name}-${opt.name}`}
                          className="mb-1"
                          label={`${opt.name} (+${Number(opt.price).toFixed(2)} BYN)`}
                          checked={new Set((selectedGroupMap[addonsModalItem._id]?.[g.name]) || []).has(opt.name)}
                          onChange={() => toggleGroupOption(addonsModalItem._id, g.name, opt.name, g.type, g.maxSelect)}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Группы опций из GroupOption */}
              {Array.isArray(addonsModalItem.groupOptionIds) && addonsModalItem.groupOptionIds.length > 0 && (
                <div className="mb-1">
                  <div className="fw-semibold mb-2">Дополнения:</div>
                  {addonsModalItem.groupOptionIds.map((groupId) => {
                    const group = groupOptions.find(g => g._id === groupId);
                    if (!group) return null;
                    
                    return (
                      <div key={group._id} className="mb-3">
                        <div className="mb-1">
                          {group.name}
                          {group.options.some(opt => opt.isRequired) && <span className="text-danger"> *</span>}
                        </div>
                        {group.options.map((opt) => (
                          <Form.Check
                            key={opt.name}
                            type={group.options.some(o => o.isMultiple) ? 'checkbox' : 'radio'}
                            name={`group-${addonsModalItem._id}-${group._id}`}
                            id={`group-${addonsModalItem._id}-${group._id}-${opt.name}`}
                            className="mb-1"
                            label={`${opt.name} ${opt.price > 0 ? `(+${Number(opt.price).toFixed(2)} BYN)` : ''}`}
                            checked={new Set((selectedGroupMap[addonsModalItem._id]?.[group.name]) || []).has(opt.name)}
                            onChange={() => toggleGroupOption(addonsModalItem._id, group.name, opt.name, opt.isMultiple ? 'multiple' : 'single', undefined)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddons}>Закрыть</Button>
          <Button 
            variant="primary" 
            onClick={() => { addToCart(addonsModalItem, selectedAddonsMap[addonsModalItem._id] || [], selectedGroupMap[addonsModalItem._id] || {}); closeAddons(); }}
            disabled={!isRestaurantOpen}
          >
            Добавить и закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Кнопка вызова официанта - только для меню в зале и только на мобильных */}
      {orderType === 'dine_in' && (
        <div
          className="bottom-button d-md-none"
          onClick={callWaiter}
          style={{
            position: 'fixed',
            bottom: 'var(--pwa-bar-height, 0px)',
            marginBottom: '50px',
            left: '20px',
            zIndex: 1000,
            borderRadius: '10px',
            width: '120px',
            height: '45px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            border: 'none',
            fontSize: '1rem',
            fontWeight: 'bold',
            transition: 'all 0.3s ease',
            backgroundColor: waiterCallSent ? '#28a745' : '#007bff',
            color: '#ffffff',
            margin: '10px',
            padding: '8px 12px',
            lineHeight: '1',
            verticalAlign: 'baseline',
            boxSizing: 'border-box',
            cursor: 'pointer',
            userSelect: 'none',
            opacity: waiterCallSent ? 0.7 : 1
          }}
          title={waiterCallSent ? "Официант вызван" : "Вызвать официанта"}
        >
          {waiterCallSent ? "✓ Вызван" : "🔔 Официант"}
        </div>
      )}

      {/* Индикатор производительности (только в режиме разработки) */}
      <PerformanceIndicator
        isSlowConnection={isSlowConnection}
        prefersReducedMotion={prefersReducedMotion}
        cacheStatus={menu ? 'loaded' : 'loading'}
        imageCount={menu?.items?.filter(item => item.image)?.length || 0}
      />
    </Container>
    </div>
  );
};

export default CustomerMenu;
